import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { AutoCompleteCompleteMethodParams } from 'primereact/autocomplete';
import scalesService from '../../service/api/ScalesService';
import suggestionsService from '../../service/api/SuggestionsService';
import FormErrorMessageScrollerRhf from '../_shared/FormErrorMessageScrollerRhf';
import { useTranslation } from 'react-i18next';
import { SuggestionOutput } from '../../types/Suggestion';
import { AutoCompleteField } from '../_shared/fields';

interface FormValues {
  operatorAutocomplete: SuggestionOutput | null;
}

const INIT_FORM_STATE: FormValues = {
  operatorAutocomplete: null,
};

type Props = {
  dialogVisible: boolean;
  closeModal: VoidFunction;
  scaleId: string;
  currentOperator: { id: string; name: string } | null;
  onSuccess: (operatorName: string | null) => void;
};

function OperatorAssignmentDialog({ dialogVisible, closeModal, scaleId, currentOperator, onSuccess }: Props) {
  const { t } = useTranslation();

  const methods = useForm<FormValues>({
    defaultValues: INIT_FORM_STATE,
  });

  const { handleSubmit, reset, setError } = methods;

  const hideDialog = () => {
    reset(INIT_FORM_STATE);
    closeModal();
  };

  const [filteredOperators, setFilteredOperators] = useState<SuggestionOutput[]>([]);

  const searchOperators = async (event: AutoCompleteCompleteMethodParams) => {
    const query = event.query;
    const suggestions = await suggestionsService.getSuggestions({ query, entity: 'users' });
    setFilteredOperators(suggestions);
  };

  useEffect(() => {
    if (dialogVisible && currentOperator) {
      reset({
        operatorAutocomplete: currentOperator.id ? { id: currentOperator.id, value: currentOperator.name } : null,
      });
    } else {
      reset(INIT_FORM_STATE);
    }
  }, [dialogVisible, currentOperator, reset]);

  const onSubmit = async (formData: FormValues) => {
    try {
      const operatorId = formData.operatorAutocomplete?.id || null;
      await scalesService.assignOperator(scaleId, operatorId);

      const operatorName = formData.operatorAutocomplete?.value || null;
      onSuccess(operatorName);
      hideDialog();
    } catch (error: any) {
      if (error.response?.status === 409) {
        const field = error.response.data.field;
        setError('operatorAutocomplete', {
          type: 'manual',
          message: t(`scaleInfo.error_${field}_already_taken`),
        });
      } else {
        setError('operatorAutocomplete', {
          type: 'manual',
          message: t('common.error_occurred'),
        });
      }
    }
  };

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <Button
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={handleSubmit(onSubmit)}
      />
    </>
  );

  return (
    <Dialog
      visible={dialogVisible}
      header={t('scaleInfo.assign_operator')}
      modal
      className="p-fluid"
      footer={DialogFooter}
      onHide={hideDialog}
      breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
      style={{ width: '40vw' }}
    >
      <FormProvider {...methods}>
        <FormErrorMessageScrollerRhf>
          <div className="col-12">
            <AutoCompleteField
              containerClass=""
              inputName="operatorAutocomplete"
              inputLabel={t('scaleInfo.operator')}
              suggestions={filteredOperators}
              field="value"
              dropdown
              completeMethod={searchOperators}
              placeholder={t('common.select')}
            />
          </div>
        </FormErrorMessageScrollerRhf>
      </FormProvider>
    </Dialog>
  );
}

export default OperatorAssignmentDialog;
